import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import {
  addAppStateChangeListener,
  type AppListenerHandle,
} from "@src/appV2/NativeAdapters/app.adapter";
import { differenceInMilliseconds } from "date-fns";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface TimerDuration {
  totalDurationMs: number;
  activeDurationMs: number;
}

export interface TimerStartOptions {
  /**
   * If set, calling `stop` after the timer has been running longer than this
   * wall-clock duration returns `undefined` and emits a `TIMER_EXPIRED` event
   * so abandonment is measured separately from successful timings.
   */
  abandonmentThresholdMs?: number;
}

interface TimerEntry {
  startDate: Date;
  backgroundMs: number;
  backgroundStartDate: Date | undefined;
  abandonmentThresholdMs: number | undefined;
}

interface TimersContextValue {
  start: (key: string, options?: TimerStartOptions) => void;
  stop: (key: string) => TimerDuration | undefined;
  isRunning: (key: string) => boolean;
}

const TimersContext = createContext<TimersContextValue | null>(null);

export function TimersProvider({ children }: PropsWithChildren) {
  const timersRef = useRef(new Map<string, TimerEntry>());

  useEffect(() => {
    let listenerHandle: AppListenerHandle | undefined;

    async function setupListener() {
      listenerHandle = await addAppStateChangeListener(({ isActive }) => {
        const now = new Date();

        for (const entry of timersRef.current.values()) {
          if (isActive) {
            if (isDefined(entry.backgroundStartDate)) {
              entry.backgroundMs += differenceInMilliseconds(now, entry.backgroundStartDate);
              entry.backgroundStartDate = undefined;
            }
          } else {
            entry.backgroundStartDate ||= now;
          }
        }
      });
    }

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
  }, []);

  const start = useCallback((key: string, options?: TimerStartOptions) => {
    timersRef.current.set(key, {
      startDate: new Date(),
      backgroundMs: 0,
      backgroundStartDate: undefined,
      abandonmentThresholdMs: options?.abandonmentThresholdMs,
    });
  }, []);

  const stop = useCallback((key: string) => {
    const entry = timersRef.current.get(key);
    timersRef.current.delete(key);

    if (!entry) {
      return undefined;
    }

    const now = new Date();
    const totalDurationMs = differenceInMilliseconds(now, entry.startDate);

    let { backgroundMs } = entry;

    if (entry.backgroundStartDate) {
      backgroundMs += differenceInMilliseconds(now, entry.backgroundStartDate);
    }

    const activeDurationMs = totalDurationMs - backgroundMs;

    if (isDefined(entry.abandonmentThresholdMs) && totalDurationMs > entry.abandonmentThresholdMs) {
      logEvent(APP_V2_USER_EVENTS.TIMER_EXPIRED, {
        key,
        abandonmentThresholdMs: entry.abandonmentThresholdMs,
        totalDurationMs,
        activeDurationMs,
      });
      return undefined;
    }

    return {
      totalDurationMs,
      activeDurationMs,
    };
  }, []);

  const isRunning = useCallback((key: string) => {
    return timersRef.current.has(key);
  }, []);

  const value = useMemo(() => ({ start, stop, isRunning }), [start, stop, isRunning]);

  return <TimersContext.Provider value={value}>{children}</TimersContext.Provider>;
}

export function useTimers(): TimersContextValue {
  const context = useContext(TimersContext);

  if (!context) {
    throw new Error("useTimers must be used within a TimersProvider");
  }

  return context;
}
